import React, { useState } from 'react';
import {
  Card, CardHeader, CardBody,
  Button, FormGroup, Label,
  Alert, Modal, ModalHeader, ModalBody, ModalFooter
} from 'reactstrap';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const RegistroCuentaPaso2 = ({ registrationId, onNext, onBack }) => {
  const [showModal, setShowModal] = useState(false);
  const [nextId, setNextId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t, i18n } = useTranslation('activation');
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'es';


  // Definición de validationSchema (copiado de tu código original)
  const validationSchema = Yup.object({
    nombres: Yup.string().required(
      t('step2.errors.firstNameRequired', 'Nombre obligatorio')
    ),
    apellidopaterno: Yup.string().required(
      t('step2.errors.lastNameFatherRequired', 'Apellido paterno obligatorio')
    ),
    apellidomaterno: Yup.string().required(
      t('step2.errors.lastNameMotherRequired', 'Apellido materno obligatorio')
    ),
    contrasena: Yup.string()
      .min(
        6,
        t(
          'step2.errors.passwordMin',
          'La contraseña debe tener al menos 6 caracteres'
        )
      )
      .required(
        t('step2.errors.passwordRequired', 'Contraseña obligatoria')
      ),
    confirmacioncontrasena: Yup.string()
      .oneOf(
        [Yup.ref('contrasena')],
        t(
          'step2.errors.passwordsMustMatch',
          'Las contraseñas deben coincidir'
        )
      )
      .required(
        t(
          'step2.errors.confirmPasswordRequired',
          'Confirma tu contraseña'
        )
      ),
  });

  // Etiquetas de los campos de nombre/apellidos
  const labelMap = {
    nombres: t('step2.firstNameLabel', 'Nombre(s)'),
    apellidopaterno: t('step2.lastNameFatherLabel', 'Apellido Paterno'),
    apellidomaterno: t('step2.lastNameMotherLabel', 'Apellido Materno'),
  };

  // Estilo para los campos de contraseña en WebKit
  const passwordInputStyle = (isVisible) => ({
    WebkitTextSecurity: isVisible ? 'none' : 'disc',
    letterSpacing: isVisible ? 'normal' : '2px',
  });

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader tag="h4" className="card-header-primary">
          {t('step2.title', 'Paso 2: Datos Personales')}
        </CardHeader>
        <CardBody>
          <Formik
            initialValues={{
              nombres: '',
              apellidopaterno: '',
              apellidomaterno: '',
              contrasena: '',
              confirmacioncontrasena: '',
            }}
            validationSchema={validationSchema}
            onSubmit={async (vals, { setSubmitting, setStatus }) => {
              setStatus(null);
              try {
                const url = `${import.meta.env.VITE_APP_API_URL}/registro/informacion/${registrationId}${currentLang ? `?lang=${currentLang}` : ''}`;
                const payload = {
                  id: registrationId,
                  ...vals,
                };

                console.log('POST Paso 2 a:', url, payload);
                const resp = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                });

                const data = await resp.json();
                console.log('Respuesta Paso 2:', data);

                // Normalizar keys
                const okBus = data.succeeded ?? data.Succeeded;
                const msg = data.message ?? data.Message;
                const nextVal = data.data ?? data.Data;

                if (resp.ok && okBus) {
                  setNextId(nextVal);
                  setShowModal(true);
                } else {
                  const baseHttpError = t('step2.errors.httpError', 'Error HTTP');
                  setStatus(msg || `${baseHttpError} ${resp.status}`);
                }
              } catch (err) {
                console.error(err);
                setStatus(
                  err.message ||
                  t('step2.errors.connectionError', 'Error de conexión')
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, status, values, setFieldValue }) => (
              <Form>
                {/* Campos de nombre/apellidos */}
                {['nombres', 'apellidopaterno', 'apellidomaterno'].map(field => (
                  <FormGroup key={field}>
                    <Label for={field}>{labelMap[field]}</Label>
                    <Field name={field} className="form-control" />
                    <ErrorMessage name={field} component="div" className="text-danger small" />
                  </FormGroup>
                ))}

                {/* Campo de contraseña */}
                <FormGroup>
                  <Label for="contrasena">{t('step2.passwordLabel', 'Contraseña')}</Label>
                  <div style={{ position: 'relative' }}>
                    <Field
                      name="contrasena"
                      type="text"  // Siempre usamos type="text"
                      className="form-control"
                      style={passwordInputStyle(showPassword)}
                    />
                    <div
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                  <ErrorMessage name="contrasena" component="div" className="text-danger small" />
                </FormGroup>

                {/* Campo de confirmación de contraseña */}
                <FormGroup>
                  <Label for="confirmacioncontrasena">{t('step2.confirmPasswordLabel', 'Confirmar Contraseña')}</Label>
                  <div style={{ position: 'relative' }}>
                    <Field
                      name="confirmacioncontrasena"
                      type="text"  // Siempre usamos type="text"
                      className="form-control"
                      style={passwordInputStyle(showConfirmPassword)}
                    />
                    <div
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                  <ErrorMessage name="confirmacioncontrasena" component="div" className="text-danger small" />
                </FormGroup>

                {/* Reglas de contraseña */}
                <Alert color="info" className="small">
                  {t('step2.passwordRules.minLength', '• Mínimo 6 caracteres')} <br />
                  {t('step2.passwordRules.oneNumber', '• Al menos 1 número')} <br />
                  {t('step2.passwordRules.oneSpecial', '• Al menos 1 caracter especial')} <br />
                  {t('step2.passwordRules.mustMatch', '• Ambos campos deben coincidir')}
                </Alert>

                {status && <Alert color="danger" className="mt-3">{status}</Alert>}

                <div className="d-flex justify-content-between mt-4">
                  <Button color="secondary" onClick={onBack}>{t('common.previous', '← Anterior')}</Button>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? t('common.saving', 'Guardando...') : t('step2.saveAndSendEmail', 'Guardar y Enviar Correo')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>

      <Modal isOpen={showModal} centered>
        <ModalHeader>{t('step2.modal.title', 'Correo Enviado')}</ModalHeader>
        <ModalBody>{t('step2.modal.body', 'Se ha enviado un correo a tu buzón con el código de activación.')}</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => { setShowModal(false); onNext(nextId); }}>
            {t('common.ok', 'OK')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RegistroCuentaPaso2;
